import { Method } from "@web-monorepo/infra/callApi";
import createActionCreator from "@web-monorepo/infra/createActionCreator";
import createActionHandler from "@web-monorepo/infra/createActionHandler";
import createAsyncActions from "@web-monorepo/infra/createAsyncActions";
import unset from "lodash/unset";
import set from "lodash/set";
import keyBy from "lodash/keyBy";
import type { AnyAction, Reducer } from "redux";
import createApiSaga from "./createApiSaga";

/*
  Creates helper functions that sets up action, action creators, an installer, an api saga, and an action handler
  for simple HTTP requests without a whole lot of custom logic

  Takes:
    stateKey: String
    actionName: String (optional)
    makePath: Function -- create the URL for the GET
    makeQuery: Function (optional) -- create any query params
    makeUpdatePath: Function -- where on the state should we update.
    makeUpdate: Function (optional) -- any transformation on the response

  Returns:
    { reducer, actionCreator, saga, actions }

  Example:
    const [fetchClassesForSchool, sagaInstaller, handler, actions] = createFetcher(
      "school_classes", // STATE_KEY
      "fetch", // action name will be "school_classes/fetch"
      ({schoolId}) => `/api/dojoSchool/${schoolId}/class`, //
      null, // no query params
      ({schoolId}) => schoolId,
    );
*/

export default function setupSetup(method: Method) {
  return function ({
    stateKey,
    actionName,
    makePath,
    makeQuery,
    makeBody,
    makeUpdate,
    makeUpdatePath,
    reducer,
  }: {
    stateKey: string;
    actionName?: string;
    makePath: (args: any) => string;
    makeBody?: (args: any) => any;
    makeQuery?: (args: any) => Record<string, string | boolean>;
    // This seems to be mandatory with the DELETE method, or rather, when we do an optimistic update
    makeUpdatePath?: (args: any, state?: Record<string, unknown>) => Array<string | number | symbol> | string;
    makeUpdate?: (payload: Record<string, unknown>, state?: Record<string, unknown>) => Record<string, unknown>;
    reducer?: Reducer;
  }) {
    if (!["GET", "POST", "PUT", "DELETE"].includes(method)) {
      throw new Error(`Method needs to be one of GET, POST, PUT, DELETE, not ${method}`);
    }

    if (method === "GET" && makeBody) {
      throw new Error("GETs shouldn't have `makeBody`");
    }

    if (method !== "GET" && makeQuery) {
      throw new Error("Non-GETs shouldn't have makeQuery");
    }

    const [START, DONE, FAIL] = createAsyncActions(`${stateKey}/${actionName || method}`);

    const actionCreator = createActionCreator(START, (arg: unknown) => arg);

    const saga = createApiSaga(
      START,
      (args: Record<string, unknown>) => ({
        method,
        path: makePath(args),
        body: makeBody ? makeBody(args) : undefined,
        query: makeQuery ? makeQuery(args) : undefined,
      }),
      DONE,
    );

    let handler;
    // for now, this is the only optimistic update we do
    if (method === "DELETE") {
      handler = createActionHandler(START, function (payload: Record<string, unknown>, state: AnyAction) {
        if (reducer) {
          return reducer(payload, state);
        }

        const path = makeUpdatePath?.(payload, state);
        if (!path) throw new Error("needs a path to delete");

        return (x: object) => unset(x, path);
      });
    } else {
      handler = createActionHandler(
        DONE,
        (payload: Record<string, unknown> & { response: { _id: string; _items?: unknown[] } }, state: AnyAction) => {
          const response = payload.response;

          // escape hatch
          if (reducer) {
            return reducer(payload, state);
          }

          const path = makeUpdatePath ? makeUpdatePath(payload, state) : response._id;
          if (!path) throw new Error("needs a path to update");

          let update: Record<string, unknown>;
          if (makeUpdate) {
            update = makeUpdate(payload, state);
          } else if (method === "GET" && response && response._items) {
            // collection update
            update = keyBy(payload.response._items, "_id");
          } else {
            update = response;
          }

          return set({ ...state }, path, update);
        },
      );
    }

    return {
      actionCreator,
      saga,
      reducer: handler,
      actions: [START, DONE, FAIL],
    };
  };
}
